<template>
  <article class="grouped-advance panel bg-lighter-grey">
    <header class="row justify-between">
      <div>
        <div
          class="advance-type"
          :class="advanceTypeClass"
        >
          {{ advanceType }}
        </div>

        <h3 class="advance-title truncate-line">
          <Loader v-if="areTitlesLoading" />
          {{ entityTitle }}
        </h3>

        <p class="advance-summary margin-0">
          <span
            v-if="isSingleReleaseAdvance && releaseDetails"
            class="text-body-light light-text"
          >
            {{ ReleaseType[releaseDetails!.musicType] }} • {{ `${$gettext('Released')} ${formatDate(releaseDetails!.releaseDate, 'MMMM YYYY')}` }} •
          </span>
          <span
            v-if="groupedAdvance.type === AdvanceType.user"
            class="text-body-light light-text"
          >
            {{ $gettext('Applied across all releases') }} •
          </span>
          <button
            class="text-button"
            @click="isExpanded = !isExpanded"
          >
            {{ $ngettext(
              '%{transactionsNumber} advance',
              '%{transactionsNumber} advances combined',
              groupedAdvance.transactions.length,
              { transactionsNumber: String(groupedAdvance.transactions.length) })
            }}
          </button>
        </p>
      </div>

      <div class="advance-total text-right">
        <span class="total-text text-body-light">
          {{ $gettext('Total Advance') }}
        </span>
        <strong class="total-amount">
          {{ formatAmount(advanceTotal, false) }}
        </strong>
      </div>
    </header>

    <figure class="bar">
      <div
        class="bar-done"
        :style="`width: ${percentageRecouped}%;`"
      />
    </figure>

    <div class="row justify-between">
      <strong class="recouped text-secondary">
        {{ formatAmount(recoupedTotal, false) }}
        {{ $gettext('recouped') }}
        {{ `(${percentageRecouped}%)` }}
      </strong>
      <span class="remaining text-right text-body-light">
        {{ formatAmount(remainingTotal, false) }}
        {{ $gettext('remaining') }}
      </span>
    </div>

    <footer class="row no-space pad-0">
      <div class="issue-date col-6">
        <small class="text-body-light light-text">
          {{ $gettext('First issued') }}
        </small>
        <strong>
          {{ formatDate(groupedAdvance.issueDate) }}
        </strong>
      </div>

      <div class="combined col-6">
        <small class="text-body-light light-text">
          {{ $gettext('Advances Combined') }}
        </small>
        <strong v-if="isShared && isAssetAdvance">
          {{ $gettext('%{tracksNumber} tracks', { tracksNumber: String(groupedAdvance.entityIds.length) }) }}
        </strong>
        <strong v-else-if="isShared">
          {{ $gettext('%{releasesNumber} releases', { releasesNumber: String(groupedAdvance.entityIds.length) }) }}
        </strong>
        <strong v-else>
          {{ $ngettext(
            '%{transactionsNumber} advance',
            '%{transactionsNumber} advances combined',
            groupedAdvance.transactions.length,
            { transactionsNumber: String(groupedAdvance.transactions.length) })
          }}
        </strong>
      </div>
    </footer>

    <ul
      v-if="isExpanded"
      class="transactions-list no-bullets pad-0"
    >
      <GroupedAdvanceTransaction
        v-for="(transaction, index) in groupedAdvance.transactions"
        :key="`transaction-${transaction.id}`"
        :advance-number="index + 1"
        :advance-type="groupedAdvance.type"
        :transaction="transaction"
      />
    </ul>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useGettext } from 'vue3-gettext'
import Loader from '@/components/CommonUI/Loader.vue'
import GroupedAdvanceTransaction from '@/components/GroupedAdvanceTransaction.vue'
import { formatAmount } from '@/helpers/formatAmount'
import { formatDate } from '@/helpers/formatDate'
import { useAdvanceStore } from '@/store'
import { AdvanceTypeNames, AdvanceType, ReleaseType } from '@/constants'

const { $gettext, $ngettext } = useGettext()

const props = defineProps<{
  groupedAdvance: AdvanceGroupedTransaction
}>()

const { getReleaseById } = useAdvanceStore()
const { advanceReleaseTitles, advanceAssetTitles } = storeToRefs(useAdvanceStore())

const isExpanded = ref(false)

const isAssetAdvance = computed(() => props.groupedAdvance.type === AdvanceType.asset)
const isReleaseAdvance = computed(() => props.groupedAdvance.type === AdvanceType.release)

const areTitlesLoading = computed(() => {
  if (isReleaseAdvance.value) {
    return props.groupedAdvance.entityIds.some(id => advanceReleaseTitles.value[id] === undefined)
  }

  if (isAssetAdvance.value) {
    return props.groupedAdvance.entityIds.some(id => advanceAssetTitles.value[id] === undefined)
  }

  return false
})
const isCancelled = computed(() => props.groupedAdvance.transactions.every(transaction => !!transaction.endsAt))
const isShared = computed(() => props.groupedAdvance.entityIds.length > 1)
const isSingleReleaseAdvance = computed(() => isReleaseAdvance.value && !isShared.value)
const releaseDetails = computed(() => getReleaseById(props.groupedAdvance.entityIds[0]))
const releaseTitle = computed(() => {
  if (!isReleaseAdvance.value || areTitlesLoading.value) return ''

  return props.groupedAdvance.entityIds
    .map(id => advanceReleaseTitles.value[id])
    .filter(Boolean)
    .join(', ')
})

const uniqueTrackTitles = computed(() => {
  if (!isAssetAdvance.value) return []

  return Array.from(new Set(props.groupedAdvance.entityIds
    .map(id => advanceAssetTitles.value[id])
    .filter(Boolean)))
})

const trackTitle = computed(() => uniqueTrackTitles.value.join(', '))

const isSharedDisplay = computed(() => {
  if (isAssetAdvance.value) {
    return uniqueTrackTitles.value.length > 1
  }

  return isShared.value
})

const entityTitle = computed(() => {
  if (props.groupedAdvance.type === AdvanceType.user) return $gettext('Account Advance')
  if (isAssetAdvance.value) return trackTitle.value

  return releaseTitle.value
})

const advanceType = computed(() => {
  if (isCancelled.value) return $gettext('Cancelled')
  if (isSharedDisplay.value && isReleaseAdvance.value) return $gettext('Shared Release')
  if (isSharedDisplay.value && isAssetAdvance.value) return $gettext('Shared Track')

  return $gettext(AdvanceTypeNames[props.groupedAdvance.type]) || $gettext('Advance')
})

const advanceTypeClass = computed(() => (
  isCancelled.value
    ? 'cancelled'
    : isSharedDisplay.value
      ? 'shared'
      : props.groupedAdvance.type
))
const advanceTotal = computed(() => props.groupedAdvance.transactions
  .reduce((total, transaction) => (total + parseFloat(transaction.amount)), 0))

const recoupedTotal = computed(() => props.groupedAdvance.transactions
  .reduce((total, transaction) => (total + parseFloat(transaction.bundledAmount)), 0))

const remainingTotal = computed(() => advanceTotal.value - recoupedTotal.value)
const percentageRecouped = computed(() => (
  remainingTotal.value === 0
    ? 100
    : parseFloat(((recoupedTotal.value / advanceTotal.value) * 100).toFixed(2))
))
</script>

<style lang="scss" scoped>
.grouped-advance {
  margin-top: 40px;
  padding: 22px 40px 32px;
}
header {
  flex-wrap: nowrap;
}
:deep(.advance-type) {
  border-radius: 14px;
  display: inline;
  font-size: 11px;
  font-weight: 700;
  padding: 6px 14px;
  text-transform: uppercase;

  &.cancelled {
    background: var(--soft-red);
    color: white;
  }

  &.release {
    background: rgba(158, 119, 255, 0.15);
    color: #9E77FF; // TODO: set as a pattern-library colour once Release Builder UI is released
  }

  &.shared {
    background: rgba(255, 177, 0, 0.15);
    color: var(--warning);
  }

  &.user,
  &.asset {
    background: rgba(106, 174, 255, 0.15);
    color: #6AAEFF; // TODO: set as a pattern-library colour once Release Builder UI is released
  }
}
.advance-title {
  font-family: var(--brand-font-body);
  font-size: 1.25rem;
  margin: .6rem 0 0;
}
.advance-total {
  flex-shrink: 0;
}
.text-button {
  position: relative;
  text-decoration: none;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &::after {
    content: " ";
    border: solid transparent;
    border-top-color: currentColor;
    border-width: 5px;
    height: 0;
    width: 0;
    position: absolute;
    right: -1rem;
    top: 50%;
    margin-top: -2px;
    z-index: 1;
  }
}
.bar,
.bar-done {
  border-radius: 4px;
  height: 8px;
}
:deep(.bar) {
  background-color: var(--brand-border);
  margin: 1rem 0;

  .bar-done {
    background: linear-gradient(90deg, #0074FF 0%, #46D7FF 100%);
  }
}
.total-amount {
  display: block;
  font-size: 2rem;
  margin-top: .5rem;
}
.total-text,
.advance-summary,
.text-button,
.recouped,
.remaining {
  font-size: 14px;
}
.issue-date,
.combined {
  small {
    display: block;
    text-transform: uppercase;
  }
  strong {
    font-size: 15px;
  }
}
.transactions-list {
  margin: 1.5rem 0 0;
}
</style>
